import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import NoDataAvailable from "@/components/NoDataAvailable/NoDataAvailable";
import {
  ArrowUpMiniIcon,
  BackArrowIcon,
  BreadCrumbChevronIcon,
  CardMiniIcon,
  ChevronRightMiniIcon,
  CrossMiniIcon,
} from "@/components/SVGComponents/MemberIcons";
import { openRazorpayCheckout } from "@/lib/razorPay/razorpayCheckout";
import toast from "@/lib/toast";
import {
  formatPrice,
  getDurationLabel,
  getQueuedPlan,
  hasActiveSubscription,
  isQueuedOnlySubscription,
} from "@/pages/Profile/Subscription/subscriptionFormatters";
import { PROFILE_BASE_PATH, PROFILE_CHILD_PATHS } from "@/pages/Profile/profilePaths";
import routes from "@/routes/routes";
import RazorpayService from "@/services/api/razorpay";
import SubscriptionService from "@/services/api/subscription";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { fetchUserSubscription } from "@/store/slices/subscriptionSlice";
import CancelPlanModal, { type CancelPlanOption } from "./CancelPlanModal";
import CancelQueuedPlanModal from "./CancelQueuedPlanModal";
import styles from "./ManageMembership.module.scss";

const subscriptionPath = `${PROFILE_BASE_PATH}/${PROFILE_CHILD_PATHS.SUBSCRIPTION}`;

const ManageMembership = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { currentSubscription: subscription } = useAppSelector((state) => state?.subscription);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isCancelQueuedModalOpen, setIsCancelQueuedModalOpen] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isCancellingQueued, setIsCancellingQueued] = useState(false);
  const [isPayingQueued, setIsPayingQueued] = useState(false);

  useEffect(() => {
    dispatch(fetchUserSubscription());
  }, [dispatch]);

  const queuedPlan = getQueuedPlan(subscription);
  const hasActivePlan = hasActiveSubscription(subscription);
  const isQueuedOnly = isQueuedOnlySubscription(subscription);
  const hasQueuedPlan = Boolean(queuedPlan);
  const hasMembershipData = hasActivePlan || hasQueuedPlan;

  const displayPlan = useMemo(() => {
    if (isQueuedOnly && queuedPlan) {
      return queuedPlan;
    }

    if (hasActivePlan && subscription) {
      return subscription;
    }

    return null;
  }, [hasActivePlan, isQueuedOnly, queuedPlan, subscription]);

  const handleCancelConfirm = async (option: CancelPlanOption) => {
    const immediate = option === "now";

    try {
      setIsCancelling(true);

      await toast.promise(
        RazorpayService.cancelSubscription(immediate).then(async (response) => {
          await dispatch(fetchUserSubscription()).unwrap();
          return response;
        }),
        {
          loading: "Cancelling subscription...",
          success: (response) => response?.message || "Subscription cancelled successfully",
          error: (error) =>
            error?.message || error?.error || "Failed to cancel subscription. Please try again.",
        },
      );
      navigate(subscriptionPath);
      setIsCancelModalOpen(false);
    } catch {
      // Toast handles the error message.
    } finally {
      setIsCancelling(false);
    }
  };

  const handleCancelQueuedConfirm = async () => {
    try {
      setIsCancellingQueued(true);

      await toast.promise(
        SubscriptionService.cancelQueuedSubscription().then(async (response) => {
          await dispatch(fetchUserSubscription()).unwrap();
          return response;
        }),
        {
          loading: "Cancelling queued plans...",
          success: (response) => response?.message || "Queued plans cancelled successfully",
          error: (error) =>
            error?.message || error?.error || "Failed to cancel queued plans. Please try again.",
        },
      );

      setIsCancelQueuedModalOpen(false);
      navigate(subscriptionPath);
    } catch {
      // Toast handles the error message.
    } finally {
      setIsCancellingQueued(false);
    }
  };

  const handlePayQueued = useCallback(async () => {
    const planId = queuedPlan?.planId;

    if (!planId) {
      toast.error("Unable to start payment. Please try again.");
      return;
    }

    try {
      setIsPayingQueued(true);

      const response = await RazorpayService.orderInitiate({
        planId,
        paymentProvider: "RAZORPAY_SUBSCRIPTION",
      });

      const { paymentOrderId, paymentKeyId } = response?.data || {};

      if (!paymentOrderId || !paymentKeyId) {
        throw new Error("Unable to start payment");
      }

      await openRazorpayCheckout({
        key: paymentKeyId,
        subscription_id: paymentOrderId,
        onSuccess: async (razorpayResponse) => {
          try {
            await RazorpayService.subscriptionCallback(razorpayResponse);
            await dispatch(fetchUserSubscription()).unwrap();
            navigate(routes?.privateRoutes?.PAYMENT_SUCCESSFUL?.path ?? "/payment-successful");
          } catch (callbackError) {
            console.error(callbackError);
            toast.error("Payment verification failed. Please contact support.");
          }
        },
        onFailure: () => {
          toast.error("Payment failed. Please try again.");
        },
      });
    } catch (error) {
      console.error(error);
      toast.error("Unable to start payment. Please try again.");
    } finally {
      setIsPayingQueued(false);
    }
  }, [dispatch, navigate, queuedPlan?.planId]);

  const actions = useMemo(
    () =>
      [
        {
          id: "change",
          title: "Change Membership",
          description: "Access more networking opportunities and higher matching limits.",
          icon: ArrowUpMiniIcon,
          onClick: () => navigate(routes?.privateRoutes?.MEMBERSHIP?.path ?? "/membership"),
          visible: hasActivePlan && !isQueuedOnly,
        },
        {
          id: "cancel",
          title: "Cancel Plan",
          description: "Cancel your subscription",
          icon: CrossMiniIcon,
          onClick: () => setIsCancelModalOpen(true),
          visible: hasActivePlan && !isQueuedOnly,
        },
        {
          id: "cancel-queued",
          title: "Cancel Queued Plans",
          description: "Cancel your queued subscriptions",
          icon: CrossMiniIcon,
          onClick: () => setIsCancelQueuedModalOpen(true),
          visible: hasQueuedPlan,
        },
        {
          id: "pay-queued",
          title: "Pay for Queued plan",
          description: "Complete payment to activate your queued membership.",
          icon: CardMiniIcon,
          onClick: handlePayQueued,
          visible: isQueuedOnly,
          disabled: isPayingQueued,
        },
      ].filter((action) => action.visible),
    [handlePayQueued, hasActivePlan, hasQueuedPlan, isPayingQueued, isQueuedOnly, navigate],
  );

  const statusClassName =
    displayPlan?.status?.toLowerCase() === "queued"
      ? styles.active
      : styles[displayPlan?.status?.toLowerCase() ?? ""];

  return (
    <section className={styles.page}>
      <div className={styles.orbLeft} />
      <div className={styles.orbRight} />
      <div className={styles.dotsRight} />

      <div className={styles.inner}>
        <div className={styles.mobileHeader}>
          <Link aria-label="Back to membership" className={styles.backButton} to={subscriptionPath}>
            <BackArrowIcon />
          </Link>
          <h1>Manage Membership</h1>
          <span className={styles.headerSpacer} />
        </div>

        <div className={styles.breadcrumbs}>
          <Link className={styles.breadcrumbLink} to={PROFILE_BASE_PATH}>
            Profile
          </Link>
          <BreadCrumbChevronIcon />
          <Link className={styles.breadcrumbLink} to={subscriptionPath}>
            Membership
          </Link>
          <BreadCrumbChevronIcon />
          <span className={styles.activeCrumb}>Manage Membership</span>
        </div>

        {hasMembershipData && displayPlan ? (
          <div className={styles.content}>
            <article className={styles.activePlanCard}>
              <div className={styles.activePlanHeader}>
                <h2>{displayPlan?.name}</h2>
                <span className={`${styles.statusBadge} ${statusClassName}`}>
                  {displayPlan?.status}
                </span>
              </div>

              <div className={styles.priceRow}>
                <strong>₹ {formatPrice(displayPlan?.amount)}</strong>
                <span>{getDurationLabel(displayPlan?.durationMonth)}</span>
              </div>

              {displayPlan?.description ? (
                <p className={styles.planDescription}>{displayPlan?.description}</p>
              ) : null}
            </article>

            <div className={styles.actionList}>
              {actions.map(({ description, disabled, icon: Icon, id, onClick, title }) => (
                <button
                  className={styles.actionCard}
                  disabled={disabled}
                  key={id}
                  onClick={onClick}
                  type="button"
                >
                  <span className={styles.actionIcon}>
                    <Icon />
                  </span>
                  <span className={styles.actionCopy}>
                    <strong>{title}</strong>
                    <span>{description}</span>
                  </span>
                  <ChevronRightMiniIcon className={styles.actionArrow} />
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className={styles.emptyStateWrap}>
            <NoDataAvailable
              description="Your membership details will appear here once your plan is active."
              note="Subscribe to a plan to manage your membership settings."
              title="No membership found"
            />
          </div>
        )}
      </div>

      <CancelPlanModal
        isSubmitting={isCancelling}
        onClose={() => setIsCancelModalOpen(false)}
        onConfirm={handleCancelConfirm}
        open={isCancelModalOpen}
      />

      <CancelQueuedPlanModal
        isSubmitting={isCancellingQueued}
        onClose={() => setIsCancelQueuedModalOpen(false)}
        onConfirm={handleCancelQueuedConfirm}
        open={isCancelQueuedModalOpen}
      />
    </section>
  );
};

export default ManageMembership;
